/**
 * ENGANCHE A LOS EMULADORES DE FIREBASE — solo para probar en la compu (E2E de la POS de la nube,
 * `npm start` contra emuladores). En producción NO PUEDE prenderse; por eso la decisión es una
 * función pura con tests (firebaseEmulators.test.js) y firebase.js solo la consulta.
 *
 * Se prende ÚNICAMENTE si se cumplen las TRES cosas:
 *   1. La página está en "localhost" o "127.0.0.1" EXACTOS. Cualquier dominio (restauradmin.web.app,
 *      restauradmin.com, un "localhost.algo.com", la IP de la LAN de un local) da null.
 *   2. El puerto NO es el de la POS del local (7070–7099): ahí la sirve el agente en la PC del
 *      local, con el MISMO build que hosting. Si alguna vez se colara un build de emuladores en el
 *      .exe, esa POS seguiría hablando con producción en vez de quedar muda.
 *   3. Alguien lo pidió de forma EXPLÍCITA:
 *        - REACT_APP_USE_EMULATORS=1 al buildear (CRA lo incrusta en el bundle). Lo usa el E2E con
 *          BUILD_PATH en una carpeta temporal, nunca build/ (hosting-guard frena el deploy de un
 *          build sellado con emuladores, por si acaso).
 *        - o, SOLO en desarrollo (`npm start`, NODE_ENV=development), `?emu=1` en la URL. Queda
 *          recordado en la pestaña (sessionStorage) para que navegar dentro de la app no lo pierda;
 *          `?emu=0` lo apaga. En un build de producción este camino ni existe.
 *
 * Los puertos son los de firebase.json (un test los compara); storage no está ahí y usa el suyo
 * por defecto (9199). El host es 127.0.0.1: los emuladores escuchan ahí, y "localhost" en algunas
 * Mac resuelve primero a ::1.
 *
 * OJO con el caché de Firestore: la base local (IndexedDB) se llama igual con o sin emuladores
 * ("firestore/[DEFAULT]/restauradmin/") y es POR ORIGEN. No mezclar en el mismo origen una sesión
 * contra producción y otra contra emuladores: usá otro puerto (el E2E usa uno propio) o una
 * ventana de incógnito. Las escrituras pendientes son por usuario y los uid del emulador no son
 * los reales, así que no se cruzan; pero los documentos cacheados sí se verían.
 */

export const EMULATOR_HOST = "127.0.0.1";

// Espejo de "emulators" en firebase.json (+ storage, que usa su puerto por defecto).
export const EMULATOR_PORTS = Object.freeze({
  auth: 9099,
  functions: 5001,
  firestore: 8080,
  database: 9000,
  storage: 9199,
});

const LOOPBACK = new Set(["localhost", "127.0.0.1"]);
// El rango en el que el agente sirve la POS del local (services/localMode.js, IS_LOCAL).
const AGENT_PORT_MIN = 7070;
const AGENT_PORT_MAX = 7099;

export const DEV_SESSION_KEY = "ra.emu";

/** ¿Qué dice la URL de desarrollo? "on" | "off" | null (no dice nada). */
export function devQueryFlag(search) {
  try {
    const v = new URLSearchParams(search || "").get("emu");
    if (v === "1") return "on";
    if (v === "0") return "off";
  } catch (_) {
    /* URL rara: como si no dijera nada */
  }
  return null;
}

/**
 * La decisión. → { host, ports } si hay que conectarse a los emuladores; null si no (producción).
 *   hostname, port   de window.location
 *   buildFlag        process.env.REACT_APP_USE_EMULATORS (incrustado al buildear)
 *   nodeEnv          process.env.NODE_ENV ("production" en todo build que sale a hosting o al .exe)
 *   search           window.location.search (solo cuenta en desarrollo)
 *   sessionFlag      lo recordado en la pestaña (solo cuenta en desarrollo)
 */
export function emulatorConfig({ hostname, port, buildFlag, nodeEnv, search, sessionFlag } = {}) {
  if (!LOOPBACK.has(String(hostname || ""))) return null;
  const p = Number(port);
  if (Number.isFinite(p) && p >= AGENT_PORT_MIN && p <= AGENT_PORT_MAX) return null;

  let wanted = String(buildFlag || "") === "1";
  if (!wanted && nodeEnv === "development") {
    const q = devQueryFlag(search);
    wanted = q === "on" || (q !== "off" && sessionFlag === "1");
  }
  if (!wanted) return null;
  return { host: EMULATOR_HOST, ports: { ...EMULATOR_PORTS } };
}
